/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import currencyEuro from '@iconify/icons-bi/currency-euro';
import envelopeFill from '@iconify/icons-bi/envelope-fill';
import starIcon from '@iconify/icons-bi/star-fill';
import usersCog from '@iconify/icons-fa-solid/users-cog';
import chartIcon from '@iconify/icons-fluent-mdl2/chart';
import cogIcon from '@iconify/icons-ion/cog';
import tileIcon from '@iconify/icons-ls/tile';
import Bell from '@iconify/icons-mdi/bell-outline';
import {Icon} from '@iconify/react';
import React from 'react';
import {Navigate, Outlet, RouteObject} from 'react-router';
import App from './app';
import CreateAccountController from './components/account/create/createAccountController';
import AccountDetailsController from './components/admin/account/accountDetailsController';
import AdminController from './components/admin/adminController';
import DefaultEmailsController from './components/admin/defaultEmails/defaultEmailsController';
import DefaultEmailEditorController from './components/admin/defaultEmails/editor/defaultEmailEditorController';
import AdminEmailSequenceController from './components/admin/emailSequence/adminEmailSequenceController';
import FailedPaymentsController from './components/admin/failedPayments/failedPaymentsController';
import HistoryImportController from './components/admin/historyImport/historyImportController';
import AdminNavigation from './components/admin/navigation/adminNavigation';
import SystemServicesController from './components/admin/systemService/systemServicesController';
import ThreadsController from './components/admin/threads/threadsController';
import AdminUsersController from './components/admin/users/adminUsersController';
import UserDetailsController from './components/admin/users/details/userDetailsController';
import UserSelectionController from './components/admin/userSelection/userSelectionController';
import PlatformUsageController from './components/admin/platformUsage/platformUsageController';
import LoginController from './components/auth/login/loginController';
import ForgotPasswordController from './components/auth/password/forgot/forgotPasswordController';
import ResetPasswordController from './components/auth/password/reset/resetPasswordController';
import RegisterController from './components/auth/register/registerController';
import ConnectionCompleteController from './components/connectionComplete/connectionCompleteController';
import DashboardController from './components/dashboard/dashboardController';
import EmailFlowController from './components/email/emailFlowController';
import TemplateEditorController from './components/email/template/editor/templateEditorController';
import UnsubscribeMonthly from './components/email/unsubscribe/unsubscribeMonthly';
import CreditInvoiceDataController from './components/invoice/credit/creditInvoiceDataController';
import InvoiceDetailsController from './components/invoice/details/invoiceDetailsController';
import EditInvoiceController from './components/invoice/edit/editInvoiceController';
import PeppolInvoicesController from './components/invoice/peppol/peppolInvoicesController';
import Maintenance from './components/maintenance/maintenance';
import AppRoute from './components/misc/appRoute';
import NotificationsPageController from './components/notifications/notificationsPageController';
import BolOauthController from './components/oauth/bol/bolOauthController';
import PdfTemplateController from './components/pdfTemplate/create/pdfTemplateController';
import PriceCalculator from './components/priceSlider/priceCalculator';
import ReferAFriendController from './components/referAFriend/referAFriendController';
import ReturnsController from './components/returns/returnsController';
import ProductReviewsController from './components/reviewAnalytics/productReviews/productReviewsController';
import ReviewDashboardController from './components/reviewAnalytics/reviewAnalyticsController';
import ReviewAnalyticsRoute from './components/reviewAnalytics/reviewAnalyticsRoute';
import SalesDashboardController from './components/salesDashboard/salesDashboardController';
import SearchVolumeController from './components/searchVolume/searchVolumeController';
import AccountUsersController from './components/settings/accountUsers/accountUsersController';
import ApiSettingsController from './components/settings/api/apiSettingsController';
import CommonSettingsController from './components/settings/common/commonSettingsController';
import EmailSettingsController from './components/settings/email/emailSettingsController';
import ExportInvoicesController from './components/settings/export/exportInvoicesController';
import InvoiceSettingsController from './components/settings/invoice/invoiceSettingsController';
import PasswordSettingsController from './components/settings/password/passwordSettingsController';
import Settings from './components/settings/settings';
import SubscriptionSettingsController from './components/settings/subscription/subscriptionSettingsController';
import VATSettingsController from './components/settings/vat/vatSettingsController';
import ConfirmIdeal from './components/subscription/ideal/confirmIdeal';
import IdealController from './components/subscription/ideal/idealController';
import ResetTwoFactorController from './components/twoFactor/reset/resetTwoFactorController';
import VatDeclarationController from './components/vatDeclaration/vatDeclarationController';
import Account from './models/accountModel';
import User from './models/userModel';
import {userHasRight} from './util/accessUtil';
import {
    ACCESS_SETTINGS_PATH, ACCESS_SETTINGS_ROUTE, ADMIN_ACCOUNT_PATH, ADMIN_DEFAULT_TEMPLATES_PATH, ADMIN_EMAILS_PATH,
    ADMIN_IMPORT_PATH, ADMIN_PATH, ADMIN_PAYMENTS_PATH, ADMIN_PLATFORM_USAGE_PATH, ADMIN_ROUTE, ADMIN_SERVICES_PATH,
    ADMIN_TEMPLATE_EDITOR_PATH, ADMIN_THREADS_PATH, ADMIN_THREADS_ROUTE, ADMIN_USER_PATH, ADMIN_USERS_PATH,
    ADMIN_USERS_ROUTE, ADMIN_USERS_SELECT_PATH, API_SETTINGS_PATH, API_SETTINGS_ROUTE, COMMON_SETTINGS_PATH,
    COMMON_SETTINGS_ROUTE, CONNECTION_COMPLETE_PATH, CREDIT_DATA_PATH, DEMO_PATH, EDIT_INVOICE_PATH, EMAIL_SEQUENCE_PATH,
    EMAIL_SETTINGS_PATH, EMAIL_SETTINGS_ROUTE, EMAIL_TEMPLATE_PATH, EMAILS_PATH, EMAILS_ROUTE, EXPORT_PATH, EXPORT_ROUTE,
    FORGOT_PASSWORD_PATH, IDEAL_COMPLETE_PATH, IDEAL_PATH, INVOICE_DETAILS_PATH, INVOICE_REQUESTS_PATH,
    INVOICE_REQUESTS_ROUTE, INVOICE_SETTINGS_PATH, INVOICE_SETTINGS_ROUTE, INVOICES_PATH, LOGIN_PATH, LOGIN_ROUTE,
    MAINTENANCE_PATH, NOTIFICATIONS_PATH, NOTIFICATIONS_ROUTE, OAUTH_PATH, ORDERS_PATH, ORDERS_ROUTE, PDF_LAYOUT_PATH,
    PDF_LAYOUT_ROUTE, PEPPOL_INVOICES_PATH, PEPPOL_INVOICES_ROUTE, PRICE_CALCULATOR_PATH, PRODUCT_SETTINGS_PATH,
    PRODUCT_SETTINGS_ROUTE, REFER_A_FRIEND_PATH, REFER_A_FRIEND_ROUTE, REGISTER_PATH, REGISTER_ROUTE,
    RESET_PASSWORD_PATH, RETURNS_PATH, RETURNS_ROUTE, REVIEW_ANALYTICS_PATH, REVIEW_ANALYTICS_ROUTE,
    SALES_DASHBOARD_PATH, SALES_DASHBOARD_ROUTE, SEARCH_TRENDS_PATH, SEARCH_VOLUMES_PATH, SEARCH_VOLUMES_ROUTE,
    SECURITY_SETTINGS_PATH, SECURITY_SETTINGS_ROUTE, SETTINGS_PATH, SETTINGS_ROUTE, SUBSCRIPTION_PATH,
    SUBSCRIPTION_ROUTE, TWO_FACTOR_RESET_PATH, UNSUBSCRIBE_MONTHLY_PATH, VAT_DECLARATION_PATH, VAT_DECLARATION_ROUTE
} from './util/constants';
import {onChangeDashboardTab} from './util/events';
import {AccessKey, BolmateSidebarItem} from './util/interfaces';
import {hasAdminLogin} from "./util/adminUtil";

function betaAuthenticator(key: string, betaStoredIn: 'user' | 'account') {
    return function(user: User | null, account: Account | null): boolean {
        if (user?.isSuperAdmin()) return true;
        if (betaStoredIn === 'user') return user?.getFlex(key) === true;
        else return account?.getFlex(key) === true;
    };
}

export const getSidebarItems = (user: User | null, account: Account | null): BolmateSidebarItem[] => {
    let items: BolmateSidebarItem[] = user ? [
        {
            index: 0,
            label: 'Facturen',
            icon: <Icon icon={tileIcon} className='icon-sm'/>,
            id: 'dashboard',
            route: ORDERS_ROUTE,
            alwaysOpen: true,
            accessKey: [
                AccessKey.PAGE_ORDERS,
                AccessKey.PAGE_INVOICE_REQUESTS,
                AccessKey.PAGE_RETURNS,
                AccessKey.PAGE_INVOICE_LAYOUT
            ],
            children: [
                {
                    index: 0,
                    label: 'Orders',
                    id: 'orders',
                    route: ORDERS_ROUTE,
                    onClick: onChangeDashboardTab,
                    accessKey: AccessKey.PAGE_ORDERS
                },
                {
                    index: 0,
                    label: 'Aanvragen',
                    id: 'invoice-requests',
                    route: INVOICE_REQUESTS_ROUTE,
                    onClick: onChangeDashboardTab,
                    accessKey: AccessKey.PAGE_INVOICE_REQUESTS
                },
                {
                    index: 0,
                    label: 'Peppol',
                    id: 'peppol-invoices',
                    route: PEPPOL_INVOICES_ROUTE,
                    accessKey: AccessKey.PAGE_ORDERS,
                    authenticator: (_: User | null, account: Account | null) => {
                        if (!account) return false;
                        if (account.vatNumber.startsWith('BE')) return true;
                        for (const vatNumber of Object.values(account.vatNumbers))
                            if (vatNumber.startsWith('BE')) return true;
                        return false;
                    }
                },
                {
                    index: 0,
                    label: 'Retouren',
                    id: 'returns',
                    route: RETURNS_ROUTE,
                    accessKey: AccessKey.PAGE_RETURNS
                },
                {
                    index: 0,
                    label: 'Layout',
                    id: 'pdf',
                    route: PDF_LAYOUT_ROUTE,
                    accessKey: AccessKey.PAGE_INVOICE_LAYOUT
                }
            ]
        },
        {
            index: 1,
            label: 'E-mails',
            icon: <Icon icon={envelopeFill} className='icon-sm'/>,
            id: 'mail-template',
            route: EMAILS_ROUTE,
            accessKey: AccessKey.PAGE_EMAIL
        },
        {
            index: 2,
            label: 'Notificaties',
            subText: 'Nieuw',
            icon: <Icon icon={Bell} className='icon-sm'/>,
            id: 'notifications',
            route: NOTIFICATIONS_ROUTE,
            accessKey: AccessKey.PAGE_NOTIFICATIONS
        },
        {
            index: 4,
            label: 'Review Inzicht',
            icon: <Icon icon={starIcon}/>,
            id: 'review-analytics',
            route: REVIEW_ANALYTICS_ROUTE,
            accessKey: [
                AccessKey.PAGE_REVIEWS_PRODUCTS,
                AccessKey.PAGE_REVIEWS_FEED,
                AccessKey.PAGE_REVIEWS_SETTINGS,
                AccessKey.PAGE_REVIEWS_EXPORT
            ]
        },
        {
            index: 6,
            label: 'Zoekvolume',
            icon: <Icon icon='pepicons-pop:loop'/>,
            id: 'search-term-dashboard',
            route: SEARCH_VOLUMES_ROUTE,
            accessKey: AccessKey.PAGE_SEARCH_TERMS
        },
        {
            index: 7,
            label: 'Instellingen',
            icon: <Icon icon={cogIcon}/>,
            id: 'settings',
            route: SETTINGS_ROUTE,
            accessKey: 'allow',
            children: [
                {
                    index: 0,
                    label: 'Algemeen',
                    id: 'commonSettings',
                    route: COMMON_SETTINGS_ROUTE,
                    accessKey: 'allow'
                },
                {
                    index: 0,
                    label: 'Gebruikers',
                    subText: 'Nieuw',
                    id: 'accountUsers',
                    route: ACCESS_SETTINGS_ROUTE,
                    accessKey: AccessKey.PAGE_ACCOUNT_USERS
                },
                {
                    index: 0,
                    label: 'Beveiliging',
                    id: 'securitySettings',
                    route: SECURITY_SETTINGS_ROUTE,
                    accessKey: 'allow'
                },
                {
                    index: 0,
                    label: 'Koppeling',
                    id: 'apiSettings',
                    route: API_SETTINGS_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_API
                },
                {
                    index: 0,
                    label: 'Producten',
                    id: 'productSettings',
                    route: PRODUCT_SETTINGS_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_PRODUCTS
                },
                {
                    index: 0,
                    label: 'Facturen',
                    id: 'invoiceSettings',
                    route: INVOICE_SETTINGS_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_INVOICE
                },
                {
                    index: 0,
                    label: 'E-mails',
                    id: 'emailSettings',
                    route: EMAIL_SETTINGS_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_EMAILS
                },
                {
                    index: 0,
                    label: 'Exporteren',
                    id: 'exportSettings',
                    route: EXPORT_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_EXPORT
                },
                {
                    index: 0,
                    label: 'Abonnement',
                    id: 'subscriptionSettings',
                    route: SUBSCRIPTION_ROUTE,
                    accessKey: AccessKey.PAGE_SETTINGS_SUBSCRIPTION
                }
            ]
        }
    ] : [
        {
            label: 'Inloggen',
            icon: 'lock',
            id: 'login',
            route: [LOGIN_ROUTE, REGISTER_ROUTE],
            index: 0,
            accessKey: 'allow'
        }
    ];
    if (user) {
        if (allowRevenueRoutes(account)) {
            items.push({
                    index: 3,
                    label: 'Sales Dashboard',
                    icon: <Icon icon={chartIcon}/>,
                    id: 'sales-dashboard',
                    route: SALES_DASHBOARD_ROUTE,
                    accessKey: AccessKey.PAGE_SALES_DASHBOARD
                },
                {
                    index: 5,
                    label: 'BTW Aangifte',
                    icon: <Icon icon={currencyEuro} className='icon-sm'/>,
                    id: 'vat-declaration',
                    route: VAT_DECLARATION_ROUTE,
                    accessKey: AccessKey.PAGE_VAT_DECLARATION
                });
        }
        if (user.isSupport()) items.push(
            {
                index: 7,
                label: 'Admin',
                icon: <Icon icon={usersCog}/>,
                id: 'admin',
                route: ADMIN_ROUTE,
                exactRoute: false,
                alwaysOpen: true,
                accessKey: 'deny',
                children: [
                    {
                        index: 1,
                        label: 'Gebruikers',
                        id: 'adminUsers',
                        route: ADMIN_USERS_ROUTE,
                        accessKey: 'deny'
                    },
                    {
                        index: 2,
                        label: 'Threads',
                        id: 'adminThreads',
                        route: ADMIN_THREADS_ROUTE,
                        accessKey: 'deny'
                    }
                ]
            }
        );
    }
    items.push(
        {
            index: 7,
            label: 'Refer a friend',
            subText: '\u20ac50',
            icon: <Icon icon='mingcute:gift-fill'/>,
            id: 'refer-a-friend',
            route: REFER_A_FRIEND_ROUTE,
            accessKey: 'allow'
        },
        {
            index: 8,
            label: 'Kennisbank',
            icon: <Icon icon='fluent:book-question-mark-20-filled'/>,
            id: 'docs',
            route: 'https://support.bolmate.nl',
            accessKey: 'allow'
        }
    );
    for (const parent of items) {
        if (!parent.children) continue;
        for (const child of parent.children)
            child.parentId = parent.id;
    }
    items.sort((itemA: BolmateSidebarItem, itemB: BolmateSidebarItem) => itemA.index - itemB.index);
    if (account && user) return filterAllowedItems(items, account, user);
    return items;
};

function filterAllowedItems(items: Array<BolmateSidebarItem>, account: Account, user: User): Array<BolmateSidebarItem> {
    const filtered: Array<BolmateSidebarItem> = [];
    for (const item of items) {
        if (item.accessKey === 'deny') {
            if (!user.isSupport()) continue;
        } // Only support allowed
        else if (Array.isArray(item.accessKey)) {
            if (!item.accessKey.find((key: AccessKey) => userHasRight(account, key)))
                continue; // Item not allowed
        } else if (item.accessKey !== 'allow' && !userHasRight(account, item.accessKey))
            continue;
        else if (item.authenticator && !item.authenticator(user, account)) continue;

        const cloned = {...item};
        if (cloned.children) cloned.children = filterAllowedItems(cloned.children, account, user);
        filtered.push(cloned);
    }
    return filtered;
}

function allowRevenueRoutes(account: Account | null): boolean {
    // Some users request that financial data is not visible in their account
    return account?.getFlex('hide_revenue') != true;
}

export function isValidPath(path: string, routes?: Array<RouteObject>): boolean {
    if (!routes) routes = Routes;
    if (path.startsWith('/')) path = path.substring(1);
    if (path.includes('?')) path = path.substring(0, path.indexOf('?'));
    return routes.some((rout: RouteObject) => {
        if (rout.path?.startsWith(path) || (rout.path && path.startsWith(rout.path))) return true;
        else if (rout.children && isValidPath(path, rout.children)) return true;
        return false;
    });
}

export const Routes: Array<RouteObject> = [
    {
        path: '*', element: <App/>, children: [
            {path: REGISTER_PATH, element: <AppRoute><RegisterController/></AppRoute>},
            {path: LOGIN_PATH, element: <AppRoute><LoginController/></AppRoute>},
            {path: DEMO_PATH, element: <Navigate to='/inloggen?demo=1'/>},
            {path: FORGOT_PASSWORD_PATH, element: <AppRoute><ForgotPasswordController/></AppRoute>},
            {path: RESET_PASSWORD_PATH, element: <AppRoute><ResetPasswordController/></AppRoute>},
            {path: TWO_FACTOR_RESET_PATH, element: <AppRoute><ResetTwoFactorController/></AppRoute>},
            {path: PRICE_CALCULATOR_PATH, element: <PriceCalculator/>},
            {path: MAINTENANCE_PATH, element: <Maintenance/>},
            {
                path: INVOICES_PATH, element: <Outlet/>, children: [
                    {
                        path: ORDERS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated><DashboardController/></AppRoute>
                    },
                    {
                        path: INVOICE_REQUESTS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_INVOICE_REQUESTS}
                                           authenticated><DashboardController/></AppRoute>
                    },
                    {
                        path: PEPPOL_INVOICES_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS}
                                           authenticated><PeppolInvoicesController/></AppRoute>
                    },
                    {path: '*', element: <Navigate to={ORDERS_ROUTE}/>}
                ]
            },
            {
                path: INVOICE_DETAILS_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS}
                                   authenticated><InvoiceDetailsController/></AppRoute>
            },
            {
                path: EDIT_INVOICE_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS}
                                   authenticated><EditInvoiceController/></AppRoute>
            },
            {
                path: PDF_LAYOUT_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_INVOICE_LAYOUT}
                                   authenticated><PdfTemplateController/></AppRoute>
            },
            {
                path: CONNECTION_COMPLETE_PATH,
                element: <AppRoute authenticated><ConnectionCompleteController/></AppRoute>
            },
            {
                path: REVIEW_ANALYTICS_PATH,
                element: <AppRoute
                    requireAccessKey={[
                        AccessKey.PAGE_REVIEWS_PRODUCTS,
                        AccessKey.PAGE_REVIEWS_FEED,
                        AccessKey.PAGE_REVIEWS_SETTINGS,
                        AccessKey.PAGE_REVIEWS_EXPORT
                    ]}
                    authenticated><ReviewAnalyticsRoute/></AppRoute>,
                children: [
                    {
                        path: ':ean',
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_REVIEWS_PRODUCTS}
                                           authenticated><ProductReviewsController/></AppRoute>
                    },
                    {
                        index: true,
                        element: <AppRoute requireAccessKey={[
                            AccessKey.PAGE_REVIEWS_PRODUCTS,
                            AccessKey.PAGE_REVIEWS_FEED,
                            AccessKey.PAGE_REVIEWS_SETTINGS,
                            AccessKey.PAGE_REVIEWS_EXPORT
                        ]} authenticated><ReviewDashboardController/></AppRoute>
                    }
                ]
            },
            {
                path: SEARCH_TRENDS_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_SEARCH_TERMS}
                                   authenticated><Navigate to={SEARCH_VOLUMES_ROUTE}/></AppRoute>
            },
            {
                path: SEARCH_VOLUMES_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_SEARCH_TERMS}
                                   authenticated><SearchVolumeController/></AppRoute>
            },
            {
                path: SALES_DASHBOARD_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_SALES_DASHBOARD}
                                   authenticated><SalesDashboardController/></AppRoute>
            },
            {
                path: SETTINGS_PATH, element: <AppRoute authenticated><Settings/></AppRoute>, children: [
                    {path: COMMON_SETTINGS_PATH, element: <CommonSettingsController/>},
                    {
                        path: ACCESS_SETTINGS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_ACCOUNT_USERS} authenticated
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><AccountUsersController/></AppRoute>
                    },
                    {path: SECURITY_SETTINGS_PATH, element: <PasswordSettingsController/>},
                    {
                        path: API_SETTINGS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_API}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><ApiSettingsController/></AppRoute>
                    },
                    {
                        path: INVOICE_SETTINGS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_INVOICE}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><InvoiceSettingsController/></AppRoute>
                    },
                    {
                        path: PRODUCT_SETTINGS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_PRODUCTS}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><VATSettingsController/></AppRoute>
                    },
                    {
                        path: SUBSCRIPTION_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><SubscriptionSettingsController/></AppRoute>
                    },
                    {
                        path: EXPORT_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_EXPORT}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><ExportInvoicesController/></AppRoute>
                    },
                    {
                        path: EMAIL_SETTINGS_PATH,
                        element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_EMAILS}
                                           fallbackRoute={COMMON_SETTINGS_ROUTE}><EmailSettingsController/></AppRoute>
                    },
                    {index: true, element: <Navigate to={COMMON_SETTINGS_ROUTE}/>}
                ]
            },
            {path: 'account-instellen', element: <AppRoute authenticated><CreateAccountController/></AppRoute>},
            {
                path: CREDIT_DATA_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated><CreditInvoiceDataController/></AppRoute>
            },
            {
                path: IDEAL_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION}
                                   authenticated><IdealController/></AppRoute>
            },
            {
                path: IDEAL_COMPLETE_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION}
                                   authenticated><ConfirmIdeal/></AppRoute>
            },
            {
                path: EMAILS_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL}
                                   authenticated><EmailFlowController/></AppRoute>
            },
            {
                path: EMAIL_SEQUENCE_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL}
                                   authenticated><Navigate to={EMAILS_ROUTE} replace/></AppRoute>
            },
            {
                path: EMAIL_TEMPLATE_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL}
                                   authenticated><TemplateEditorController/></AppRoute>
            },
            {
                path: RETURNS_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_RETURNS}
                                   authenticated><ReturnsController/></AppRoute>
            },
            {
                path: VAT_DECLARATION_PATH,
                element: <AppRoute requireAccessKey={AccessKey.PAGE_VAT_DECLARATION}
                                   authenticated><VatDeclarationController/></AppRoute>
            },
            {path: UNSUBSCRIBE_MONTHLY_PATH, element: <AppRoute><UnsubscribeMonthly/></AppRoute>},
            {
                path: ADMIN_PATH, element: <AppRoute authenticated><AdminController/></AppRoute>, children: [
                    {path: ADMIN_USER_PATH, element: <UserDetailsController/>},
                    {path: ADMIN_ACCOUNT_PATH, element: <AccountDetailsController/>},
                    {path: ADMIN_THREADS_PATH, element: <ThreadsController/>},
                    {path: ADMIN_USERS_PATH, element: <AdminUsersController/>},
                    {path: ADMIN_SERVICES_PATH, element: <SystemServicesController/>},
                    {path: ADMIN_IMPORT_PATH, element: <HistoryImportController/>},
                    {path: ADMIN_EMAILS_PATH, element: <AdminEmailSequenceController/>},
                    {path: ADMIN_PAYMENTS_PATH, element: <FailedPaymentsController/>},
                    {path: ADMIN_USERS_SELECT_PATH, element: <UserSelectionController/>},
                    {path: ADMIN_DEFAULT_TEMPLATES_PATH, element: <DefaultEmailsController/>},
                    {path: ADMIN_TEMPLATE_EDITOR_PATH, element: <DefaultEmailEditorController/>},
                    {path: ADMIN_PLATFORM_USAGE_PATH, element: <PlatformUsageController/>},
                    {element: <AdminNavigation/>, index: true}
                ]
            },
            {path: OAUTH_PATH, element: <BolOauthController/>},
            {path: REFER_A_FRIEND_PATH, element: <AppRoute authenticated><ReferAFriendController/></AppRoute>},
            {
                path: NOTIFICATIONS_PATH, element: <AppRoute requireAccessKey={AccessKey.PAGE_NOTIFICATIONS}
                                                             authenticated><NotificationsPageController/></AppRoute>
            },
            {path: '*', element: <Navigate to='/facturen/orders'/>}
        ]
    }
];